import { Phone, Mail, MapPin, Clock, MessageCircle, ExternalLink } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BUSINESS, waLink } from "@/lib/businessInfo";

const Contact = () => {
    const { lang, t } = useLanguage();
    const gu = lang === "gu" ? "font-gujarati" : "";

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-24 md:py-32 bg-[#F8FAFC] relative"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-3xl">
                    <span
                        className={`inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#0B308A] ${gu}`}
                    >
                        {t("contact.overline")}
                    </span>
                    <h2
                        className={`mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 leading-[1.1] ${gu}`}
                    >
                        {t("contact.title")}
                    </h2>
                    <p className={`mt-5 text-slate-600 text-lg leading-relaxed ${gu}`}>
                        {t("contact.subtitle")}
                    </p>
                </div>

                <div className="mt-14 grid lg:grid-cols-12 gap-8">
                    {/* Contact details */}
                    <div className="lg:col-span-5 space-y-5">
                        <div className="bg-white border border-slate-100 p-6 rounded-sm shadow-sm">
                            <div className="flex items-start gap-4">
                                <div className="w-11 h-11 rounded-sm bg-[#EFF6FF] text-[#0B308A] flex items-center justify-center shrink-0">
                                    <MapPin className="w-5 h-5" />
                                </div>
                                <div>
                                    <div
                                        className={`text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 ${gu}`}
                                    >
                                        {t("contact.address_label")}
                                    </div>
                                    <div
                                        data-testid="contact-address"
                                        className="mt-1 text-slate-900 font-medium text-[15px] leading-relaxed"
                                    >
                                        {BUSINESS.address}
                                    </div>
                                    <a
                                        href={BUSINESS.mapLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0B308A] hover:underline"
                                    >
                                        Open in Google Maps
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white border border-slate-100 p-6 rounded-sm shadow-sm">
                            <div className="flex items-start gap-4">
                                <div className="w-11 h-11 rounded-sm bg-[#EFF6FF] text-[#0B308A] flex items-center justify-center shrink-0">
                                    <Phone className="w-5 h-5" />
                                </div>
                                <div className="flex-1">
                                    <div
                                        className={`text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 ${gu}`}
                                    >
                                        {t("contact.phone_label")}
                                    </div>
                                    <div className="mt-2 space-y-1.5">
                                        <a
                                            data-testid="contact-phone-1"
                                            href={`tel:${BUSINESS.phoneTel[0]}`}
                                            className="block font-display text-xl font-bold text-slate-900 hover:text-[#0B308A] transition-colors"
                                        >
                                            {BUSINESS.phones[0]}
                                        </a>
                                        <a
                                            data-testid="contact-phone-2"
                                            href={`tel:${BUSINESS.phoneTel[1]}`}
                                            className="block font-display text-lg font-semibold text-slate-700 hover:text-[#0B308A] transition-colors"
                                        >
                                            {BUSINESS.phones[1]}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-5">
                            <div className="bg-white border border-slate-100 p-6 rounded-sm shadow-sm">
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-sm bg-[#EFF6FF] text-[#0B308A] flex items-center justify-center shrink-0">
                                        <Mail className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div
                                            className={`text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 ${gu}`}
                                        >
                                            {t("contact.email_label")}
                                        </div>
                                        <a
                                            data-testid="contact-email"
                                            href={`mailto:${BUSINESS.email}`}
                                            className="mt-1 block text-[14px] text-slate-900 font-medium hover:text-[#0B308A] break-all"
                                        >
                                            {BUSINESS.email}
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white border border-slate-100 p-6 rounded-sm shadow-sm">
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-sm bg-[#EFF6FF] text-[#0B308A] flex items-center justify-center shrink-0">
                                        <Clock className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div
                                            className={`text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 ${gu}`}
                                        >
                                            {t("contact.hours_label")}
                                        </div>
                                        <div
                                            className={`mt-1 text-[14px] text-slate-900 font-medium ${gu}`}
                                        >
                                            {t("contact.hours_value")}
                                        </div>
                                        <div
                                            className={`text-[12.5px] text-slate-500 mt-0.5 ${gu}`}
                                        >
                                            {t("contact.sunday")}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            <a
                                data-testid="contact-call-btn"
                                href={`tel:${BUSINESS.phoneTel[0]}`}
                                className={`flex-1 inline-flex items-center justify-center gap-2 bg-[#0B308A] text-white hover:bg-[#1E3A8A] px-6 py-4 rounded-sm font-semibold text-[14px] shadow-[0_10px_24px_rgba(11,48,138,0.25)] transition-all ${gu}`}
                            >
                                <Phone className="w-4 h-4" />
                                {t("contact.cta_call")}
                            </a>
                            <a
                                data-testid="contact-wa-btn"
                                href={waLink()}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white hover:bg-[#1DA851] px-6 py-4 rounded-sm font-semibold text-[14px] shadow-[0_10px_24px_rgba(37,211,102,0.3)] transition-all ${gu}`}
                            >
                                <MessageCircle className="w-4 h-4" />
                                {t("contact.cta_whatsapp")}
                            </a>
                        </div>
                    </div>

                    {/* Map */}
                    <div className="lg:col-span-7">
                        <div className="bg-white border border-slate-100 p-3 rounded-sm shadow-sm h-full">
                            <div
                                className={`px-3 pt-3 pb-4 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#0B308A] ${gu}`}
                            >
                                {t("contact.map_title")}
                            </div>
                            <iframe
                                data-testid="google-map"
                                title="Shree Ganesh Financial Service - Office Location"
                                src={BUSINESS.mapEmbed}
                                width="100%"
                                height="520"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                className="rounded-sm w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
